import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, QrCode, Store, X, Apple, PlayCircle, Globe, ExternalLink, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

const VendorsCTA: React.FC = () => {
  const [showQRModal, setShowQRModal] = useState(false);

  const getSmartVendorQRCodeURL = () => {
    return 'https://dailzy.com/vendor-app';
  };

  const handleQRClick = () => {
    setShowQRModal(true);
  };

  return (
    <>
      <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-red-50">
        <div className="container mx-auto px-4">
          {/* Main CTA Section */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Ready to <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Start</span> Your Journey?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Join thousands of successful vendors and start growing your business with Dailzy today.
            </p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
            >
              <motion.button
                className="group relative bg-gradient-to-r from-red-500 via-red-600 to-orange-500 hover:from-red-600 hover:via-red-700 hover:to-orange-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl transition-all duration-300 overflow-hidden"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10 flex items-center">
                  Start Onboarding Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </span>
              </motion.button>
              
              <Link to="/contact">
                <motion.button
                  className="border-2 border-red-500 text-red-500 hover:bg-red-50 px-8 py-4 rounded-2xl font-semibold transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Contact Support Team
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Download App Section */}
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-red-500 via-red-600 to-orange-500 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-16 -translate-x-16"></div>
              
              <div className="relative z-10">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Left Side - Content */}
                  <div className="text-center md:text-left">
                    <motion.div
                      className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-2xl px-4 py-2 mb-6"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      viewport={{ once: true }}
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm font-medium">Get Started Today</span>
                    </motion.div>
                    
                    <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                      Download Dailzy Saathi App
                    </h3>
                    <p className="text-white/90 text-lg mb-6">
                      Everything you need to manage your business in one powerful app. Start selling in minutes!
                    </p>
                    
                    {/* Quick Stats */}
                    {/* <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                        <div className="text-2xl font-bold">15K+</div>
                        <div className="text-sm text-white/80">Active Vendors</div>
                      </div>
                      <div className="text-center p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                        <div className="text-2xl font-bold">4.9★</div>
                        <div className="text-sm text-white/80">App Rating</div>
                      </div>
                    </div> */}
                  </div>

                  {/* Right Side - QR Code */}
                  <motion.div
                    className="text-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm mx-auto">
                      {/* <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Store className="w-8 h-8 text-white" />
                      </div> */}
                      
                      <h4 className="text-xl font-bold text-gray-800 mb-2">Scan QR Code</h4>
                      <p className="text-gray-600 text-sm mb-6">Download directly to your phone</p>
                      
                      {/* QR Code */}
                      <div className="w-48 h-48 bg-gray-50 border-2 border-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(getSmartVendorQRCodeURL())}`}
                          alt="QR Code for Dailzy Vendor App" 
                          className="w-44 h-44 rounded-xl"
                        />
                      </div>
                      
                      <button
                        onClick={handleQRClick}
                        className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <QrCode className="w-5 h-5" />
                        View Full Size
                      </button>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* QR Code Modal */}
      <AnimatePresence>
        {showQRModal && (
          <motion.div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowQRModal(false)}
          >
            <motion.div 
              className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setShowQRModal(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Store className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Download Dailzy Saathi</h3>
                <p className="text-gray-600 mb-6">Scan with your phone to download the vendor app</p>
                
                {/* Large QR Code */}
                <div className="w-64 h-64 bg-white border-4 border-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(getSmartVendorQRCodeURL())}`}
                    alt="QR Code for Dailzy Vendor App" 
                    className="w-56 h-56 rounded-xl"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <Apple className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                    <div className="text-sm font-medium text-gray-800">iOS App Store</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <PlayCircle className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                    <div className="text-sm font-medium text-gray-800">Google Play</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Globe className="w-4 h-4" />
                  <span>dailzy.com/vendor-app</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VendorsCTA; 